import { Component } from '@theme/component';

/**
 * Wishlist storage.
 *
 * Saved per browser via localStorage -- there is no server-side account to
 * write to yet (this store uses Shopify's hosted customer accounts, which
 * gives a theme no way to write to a shopper's account on its own; that
 * needs a small companion app). Every heart button on the page, and the
 * wishlist page itself, reads this same list and re-renders when it changes,
 * so a product hearted on a collection card immediately shows as hearted on
 * its own product page too.
 *
 * @typedef {{ id: string, handle: string }} WishlistEntry
 */

const STORAGE_KEY = 'altior:wishlist';
const CHANGE_EVENT = 'wishlist:change';

export const WishlistStore = {
  /** @returns {WishlistEntry[]} */
  all() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((entry) => entry && entry.id && entry.handle) : [];
    } catch (_) {
      return [];
    }
  },

  /** @param {string} id */
  has(id) {
    return this.all().some((entry) => entry.id === String(id));
  },

  /**
   * @param {string} id
   * @param {string} handle
   * @returns {boolean} the new saved state
   */
  toggle(id, handle) {
    const current = this.all();
    const idStr = String(id);
    const exists = current.some((entry) => entry.id === idStr);
    const next = exists ? current.filter((entry) => entry.id !== idStr) : [...current, { id: idStr, handle }];

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (_) {
      // Storage can throw in a private tab with site data blocked -- the
      // toggle still dispatches below so the UI reflects this session's
      // intent even though it won't survive a reload.
    }

    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { entries: next } }));
    return !exists;
  },

  /** @param {string} id */
  remove(id) {
    const next = this.all().filter((entry) => entry.id !== String(id));
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (_) {
      /* no-op */
    }
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { entries: next } }));
  },

  /**
   * @param {(entries: WishlistEntry[]) => void} callback
   * @returns {() => void} unsubscribe
   */
  subscribe(callback) {
    /** @param {Event} event */
    const handler = (event) => callback(/** @type {CustomEvent} */ (event).detail.entries);
    window.addEventListener(CHANGE_EVENT, handler);
    // A save made in another tab only reaches this one as a native `storage`
    // event, so that's listened for too, re-reading from localStorage since
    // the other tab's list isn't handed over directly.
    const storageHandler = (/** @type {StorageEvent} */ event) => {
      if (event.key === STORAGE_KEY) callback(this.all());
    };
    window.addEventListener('storage', storageHandler);
    return () => {
      window.removeEventListener(CHANGE_EVENT, handler);
      window.removeEventListener('storage', storageHandler);
    };
  },
};

/**
 * The heart toggle button. Placed on product cards and the PDP sticky bar --
 * every instance for the same product id stays in sync with the others via
 * WishlistStore's change event, so hearting a product on a collection card
 * shows it as saved on the product page too, without a reload.
 *
 * @extends {Component}
 */
class WishlistHeartButton extends Component {
  /** @type {(() => void) | null} */
  #unsubscribe = null;

  connectedCallback() {
    super.connectedCallback();
    this.#render(WishlistStore.has(this.productId));
    this.#unsubscribe = WishlistStore.subscribe(() => this.#render(WishlistStore.has(this.productId)));
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unsubscribe?.();
    this.removeEventListener('click', this.#handleClick);
  }

  get productId() {
    return this.getAttribute('data-product-id') ?? '';
  }

  get productHandle() {
    return this.getAttribute('data-product-handle') ?? '';
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!this.productId || !this.productHandle) return;
    const saved = WishlistStore.toggle(this.productId, this.productHandle);
    // A short pop on the icon itself, not a page-level toast -- the filled
    // heart is already the confirmation, and this sits on a product card
    // inside a scrolling grid where a toast would as often cover the next
    // row as be seen.
    if (saved) {
      this.classList.add('wishlist-heart--pop');
      this.addEventListener('animationend', () => this.classList.remove('wishlist-heart--pop'), { once: true });
    }
  };

  /** @param {boolean} saved */
  #render(saved) {
    this.classList.toggle('wishlist-heart--saved', saved);
    this.setAttribute('aria-pressed', String(saved));
    const label = saved ? this.dataset.labelSaved : this.dataset.labelSave;
    if (label) this.setAttribute('aria-label', label);
  }
}

/**
 * Small count badge for the header wishlist link. Hidden at zero.
 *
 * @extends {Component}
 */
class WishlistCountBadge extends Component {
  /** @type {(() => void) | null} */
  #unsubscribe = null;

  connectedCallback() {
    super.connectedCallback();
    this.#render(WishlistStore.all().length);
    this.#unsubscribe = WishlistStore.subscribe((entries) => this.#render(entries.length));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unsubscribe?.();
  }

  /** @param {number} count */
  #render(count) {
    this.textContent = count > 99 ? '99+' : String(count);
    this.classList.toggle('hidden', count === 0);
  }
}

/**
 * The /pages/wishlist grid. Client-rendered: the saved list lives in
 * localStorage, which Liquid can never see server-side, so this fetches each
 * saved product's public `.js` endpoint (no auth needed) and builds cards
 * from the current price/availability rather than trusting a stale snapshot.
 *
 * @extends {Component}
 */
class WishlistGrid extends Component {
  /** @type {(() => void) | null} */
  #unsubscribe = null;

  connectedCallback() {
    super.connectedCallback();
    this.#load();
    this.#unsubscribe = WishlistStore.subscribe(() => this.#load());
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unsubscribe?.();
    this.removeEventListener('click', this.#handleClick);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const removeButton = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-wishlist-remove]') : null
    );
    if (!removeButton) return;
    event.preventDefault();
    const id = removeButton.getAttribute('data-wishlist-remove');
    if (id) WishlistStore.remove(id);
  };

  async #load() {
    const entries = WishlistStore.all();
    const emptyState = this.querySelector('[data-wishlist-empty]');
    const grid = this.querySelector('[data-wishlist-items]');
    if (!grid) return;

    if (entries.length === 0) {
      grid.innerHTML = '';
      emptyState?.removeAttribute('hidden');
      return;
    }
    emptyState?.setAttribute('hidden', '');

    const products = await Promise.all(
      entries.map(async (entry) => {
        try {
          const response = await fetch(`/products/${entry.handle}.js`);
          if (!response.ok) return null;
          return await response.json();
        } catch (_) {
          return null;
        }
      })
    );

    // A product can be unpublished or deleted after it was saved; drop it
    // from the grid silently rather than showing a broken card. It stays
    // out of the saved list too, so it doesn't keep failing to load.
    const missingIds = entries.filter((_, index) => products[index] === null).map((entry) => entry.id);
    missingIds.forEach((id) => WishlistStore.remove(id));

    grid.innerHTML = products
      .filter((product) => product !== null)
      .map((product) => this.#cardMarkup(product))
      .join('');
  }

  /**
   * @param {{
   *   id: number, handle: string, title: string, url: string, available: boolean,
   *   price: number, compare_at_price: number|null, featured_image: string|null,
   *   images: string[]
   * }} product
   */
  #cardMarkup(product) {
    const image = product.featured_image ?? product.images?.[0] ?? null;
    const imageUrl = image ? `${image}${image.includes('?') ? '&' : '?'}width=600` : '';
    const onSale = product.compare_at_price && product.compare_at_price > product.price;

    return `
      <div class="wishlist-card">
        <a class="wishlist-card__media" href="${this.#escape(product.url)}">
          ${
            imageUrl
              ? `<img src="${this.#escape(imageUrl)}" alt="${this.#escape(product.title)}" loading="lazy" width="600" height="800">`
              : ''
          }
          ${product.available === false ? `<span class="wishlist-card__badge">${this.dataset.soldOutLabel ?? 'Sold out'}</span>` : ''}
        </a>
        <div class="wishlist-card__info">
          <a class="wishlist-card__title" href="${this.#escape(product.url)}">${this.#escape(product.title)}</a>
          <span class="wishlist-card__price">
            ${this.#formatMoney(product.price)}
            ${onSale ? `<s class="wishlist-card__compare">${this.#formatMoney(product.compare_at_price)}</s>` : ''}
          </span>
        </div>
        <button
          type="button"
          class="wishlist-card__remove"
          data-wishlist-remove="${product.id}"
          aria-label="${this.dataset.removeLabel ?? 'Remove from wishlist'}"
        >&times;</button>
      </div>
    `;
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    const amount = cents / 100;
    const currency = this.dataset.currency ?? 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (_) {
      return amount.toFixed(2);
    }
  }

  /** @param {string} value */
  #escape(value) {
    return String(value).replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
  }
}

if (!customElements.get('wishlist-heart-button')) {
  customElements.define('wishlist-heart-button', WishlistHeartButton);
}
if (!customElements.get('wishlist-count-badge')) {
  customElements.define('wishlist-count-badge', WishlistCountBadge);
}
if (!customElements.get('wishlist-grid')) {
  customElements.define('wishlist-grid', WishlistGrid);
}
